'use client';

import { FC, useContext, useMemo } from 'react';
import { Button, ButtonGroup, Dialog, Flex } from '@/_components';
import { RemoveAllTagsDialogProps } from './RemoveAllTagsDialog.types';
import { updateBulkClientTags, updateBulkEntityTags } from '@/_api/admin-tags';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { EntityV2Context } from '@/_contexts/admin/EntityV2Provider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { ENTITY } from '../EditTagsDialog.types';
import { VERBIAGE } from '../EditTagsDialog.language';

export const RemoveAllTagsDialog: FC<RemoveAllTagsDialogProps> = ({
  entityOrUserIds,
  isOpen,
  onClose,
  parentOnClose,
  tagType,
}) => {
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { loadUsersForTable } = useContext(CompanyContext);
  const { loadEntities } = useContext(EntityV2Context);
  const { clientTagOptionsToAdd, entityTagOptionsToAdd } =
    useContext(FirmSettingsContext);

  const updateTags =
    tagType === ENTITY ? updateBulkEntityTags : updateBulkClientTags;
  const tagOptions = useMemo(
    () => (tagType === ENTITY ? entityTagOptionsToAdd : clientTagOptionsToAdd),
    [clientTagOptionsToAdd, entityTagOptionsToAdd, tagType],
  );
  const reloadTable = tagType === ENTITY ? loadEntities : loadUsersForTable;

  const onRemoveAllTags = async () => {
    try {
      await updateTags({
        ids: entityOrUserIds,
        // we can't just pass an empty array here; API expects list of exact tag names to remove
        tag_names: tagOptions
          .map((tag) => tag.name)
          .filter((name) => name !== undefined),
        remove: true,
        token,
      });

      await reloadTable();
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      onClose();
      parentOnClose();
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Remove all tags?"
      isNarrow={true}
    >
      <Dialog.Content>
        You are about to remove all tags from the{' '}
        {entityOrUserIds.length > 1 ? (
          <b>{entityOrUserIds.length}</b>
        ) : undefined}{' '}
        selected{' '}
        {VERBIAGE[tagType][
          entityOrUserIds.length > 1 ? 'plural' : 'singular'
        ].toLowerCase()}
        . This action cannot be undone.
      </Dialog.Content>
      <Dialog.Footer>
        <Flex xAlign="end">
          <ButtonGroup>
            <Button intent="secondary" value="Cancel" onClick={onClose} />
            <Button
              intent="danger"
              iconLeft="trash"
              value="Remove all"
              onClick={onRemoveAllTags}
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Footer>
    </Dialog>
  );
};
